import {computed, defineComponent, ref, watch} from 'vue';
import * as KlipApi from "@/api/klip-api.proxy";
import {useKlipApiProxy} from "@/plugins/proxy-client";
import {useKlModalEventsUtil} from "@/app/shared/helpers/kl-modal-events-util";
import {useForm} from "vee-validate";
import {isEmpty} from "lodash-es";
import {useUserStore} from "@/app/shared/state/UserDataModule";
import {EUserRole} from "@/app/shared/state/user-role";
import {RouteRecordRaw} from "vue-router";
import {AdminOrganisationDetail} from "@/app/admin/admin-routes";
import * as yup from 'yup';
import {toTypedSchema} from '@vee-validate/yup';
import {
    klValidationFields,
    ValidationFieldsType
} from '@/app/shared/components/kl-form-fields/kl-field-validation-util';

export default defineComponent({
    name: 'KlOrganisationPaymentSettings',
    emits: ['update:refreshOrganisation','update:changeInvoiceType'],
    props: {
        organisation: {
            type: Object as () => KlipApi.OrganisationDetail,
            required: true,
        },
        invoiceData: {
            type: Object as () => KlipApi.OrganisationInvoiceData,
            required: true,
        }
    },
    setup(props, { emit }) {

        const schema = yup.object({
            email: yup.string().required().max(320).email().label('E-mail'),
            attention: yup.string().notRequired().max(200).label('Ter attentie van'),
            reference: yup.string().notRequired().max(25).label('Eigen referentie'),
            invoiceContactName: yup.string().notRequired().max(200).label('Naam'),
            invoiceContactEmail: yup.string().notRequired().max(320).email().label('Contactpersoon e-mail'),
            invoiceContactPhoneNumber: yup.string().notRequired().kl_phone().label('Telefoon'),
        });

        const togglePaysByInvoiceModal = 'toggle-pays-by-invoice';
        const invoiceDataModal = 'invoice-data';

        const klipApiProxy = useKlipApiProxy();
        const paysByInvoiceModalEvents = useKlModalEventsUtil(togglePaysByInvoiceModal, {
            onClosed() {
                isSending.value = false;
            },
        });
        const invoiceDataModalEvents = useKlModalEventsUtil(invoiceDataModal, {
            onClosed() {
                isSending.value = false;
            },
        });

        const form = useForm({
            validationSchema: toTypedSchema(schema),
        });
        const fields: ValidationFieldsType<typeof schema> = klValidationFields<typeof schema>(schema);

        const disableSubmit = computed((): boolean => !form.meta.value.valid);

        const organisationInvoiceData = ref<KlipApi.OrganisationInvoiceData>(null);
        watch(() => organisationInvoiceData.value?.email, (newValue) => { fields.email.value.value = newValue; },{ flush: 'sync' });
        watch(() => organisationInvoiceData.value?.attention, (newValue) => { fields.attention.value.value = newValue; },{ flush: 'sync' });
        watch(() => organisationInvoiceData.value?.reference, (newValue) => { fields.reference.value.value = newValue; },{ flush: 'sync' });
        watch(() => organisationInvoiceData.value?.invoiceContactName, (newValue) => { fields.invoiceContactName.value.value = newValue; },{ flush: 'sync' });
        watch(() => organisationInvoiceData.value?.invoiceContactEmail, (newValue) => { fields.invoiceContactEmail.value.value = newValue; },{ flush: 'sync' });
        watch(() => organisationInvoiceData.value?.invoiceContactPhoneNumber, (newValue) => { fields.invoiceContactPhoneNumber.value.value = newValue; },{ flush: 'sync' });


        const viewModelLoading = ref<boolean>(true);
        const isSending = ref<boolean>(false);
        const isSendingDelete = ref<boolean>(false);
        const fetchingAthumiData = ref<boolean>(false);
        const switchingToOnlinePayment = ref<boolean>(false);
        const organisationPaysByInvoiceChanged = ref<boolean>(false);
        const organisationDetail = ref<KlipApi.OrganisationDetail>(null);
        const organisationPaysByInvoice = ref<boolean>(false);
        const athumiId = ref<string>('');

        const subOrganisationsCount = computed(() => {
            if (!organisationDetail.value.organisationsDataTable) {
                return 0;
            }

            if (isEmpty(organisationDetail.value.organisationsDataTable) || organisationDetail.value.organisationsDataTable.length === 1) {
                return 0;
            }
            return organisationDetail.value.organisationsDataTable.length;
        });

        const prepaidAmount = computed((): string => {
            const formatter = new Intl.NumberFormat('nl-BE', {
                style: 'currency',
                currency: 'EUR'
            });
            return formatter.format(organisationInvoiceData.value.prepaidAmount);
        });

        const isAdmin = computed((): boolean => {
            return useUserStore().hasRole([EUserRole.admin]);
        });

        const switchToInvoiceDisabled = computed((): boolean => {
            if(!isAdmin.value) {
                return true;
            }
            return !organisationInvoiceData.value.athumiData.hasValidAthumiId
        });

        const hasValidAthumiId = computed((): boolean => {
            if(organisationInvoiceData.value.athumiData) {
                return organisationInvoiceData.value.athumiData.hasValidAthumiId
            }
            return false;
        });

        const hasValidPaymentData = computed((): boolean => {
            return organisationInvoiceData.value.hasInvoiceData && hasValidAthumiId.value;
        })

        const athumiActor = computed((): KlipApi.OrganisatieAthumiData => {
            return organisationInvoiceData.value.athumiData;
        });

        const onSaveActor = async (newActor: KlipApi.OrganisatieAthumiData) => {

            isSending.value = true;

            const input = KlipApi.UpdateOrganisationActorInput.fromJS({});

            input.organisationId = props.organisation.organisationId;
            input.name = props.organisation.organisationName;

            input.vatNumber = newActor.vatNumber;

            input.street = newActor.straat;
            input.streetNumber = newActor.nummer;
            input.box = newActor.bus;
            input.zip = newActor.postcode;
            input.city = newActor.gemeente;
            input.countryCode = newActor.countryCode;
            input.invoiceEmail = newActor.emailAdressen[0];
            fetchingAthumiData.value = true;

            await klipApiProxy.operations_CreateOrUpdateOrganisationActor(input);

            emit('update:refreshOrganisation');

            isSending.value = false;
        }

        const routeAdminOrganisationDetail = computed((): RouteRecordRaw => {
            return AdminOrganisationDetail;
        });

        const _saveOrganisationInvoiceData = async () => {
            isSending.value = true;

            const input = KlipApi.UpdateOperationInvoiceDataInput.fromJS({})
            input.organisationId = props.organisation.organisationId;
            input.reference = organisationInvoiceData.value.reference;
            input.attention = organisationInvoiceData.value.attention;
            input.vatNumber = organisationInvoiceData.value.athumiData.vatNumber;
            input.email = organisationInvoiceData.value.email;
            input.updateChildOrganisations = true;
            input.invoiceName = organisationInvoiceData.value.invoiceContactName;
            input.invoiceEmail = organisationInvoiceData.value.invoiceContactEmail;
            input.invoicePhoneNumber = organisationInvoiceData.value.invoiceContactPhoneNumber;

            await klipApiProxy.operations_UpdateOrganisationInvoiceDetail(input);
            organisationInvoiceData.value.hasInvoiceData = true;
            organisationInvoiceData.value.paysByInvoice = true;
            toggleCheckbox(true);
            isSending.value = false;
            emit('update:changeInvoiceType');
            invoiceDataModalEvents.triggerModalToggle();
        };

        const saveOrganisationPaysByInvoice = async () => {
            if (organisationPaysByInvoice.value === false) {
                paysByInvoiceModalEvents.triggerModalToggle();

                isSending.value = true;
            } else {
                await switchToInvoice();
            }
        };

        const toggleCheckbox = (checked: boolean) => {
            organisationPaysByInvoice.value = checked;
            organisationPaysByInvoiceChanged.value = organisationPaysByInvoice.value !== organisationInvoiceData.value.paysByInvoice;
        };

        const changePaymentType = async () => {
            isSending.value = true;
            const input = KlipApi.UpdateOrganisationPaymentSettingsInput.fromJS({});
            input.organisationId = props.organisation.organisationId;
            input.paysByInvoice = organisationPaysByInvoice.value;
            await klipApiProxy.operations_UpdateOrganisationPaymentSettings(input);
            isSending.value = false;
        }

        const switchToOnlinePayment = async () => {
            switchingToOnlinePayment.value = true;
            await changePaymentType();
            organisationInvoiceData.value.paysByInvoice = organisationPaysByInvoice.value;
            switchingToOnlinePayment.value = false;

            paysByInvoiceModalEvents.triggerModalToggle();
        };

        const switchToInvoice = async () => {
            await changePaymentType();
            organisationInvoiceData.value.paysByInvoice = organisationPaysByInvoice.value;
        };

        const onOpenSwitchToInvoiceModal = () => {
            form.resetForm({ values: form.values });
            invoiceDataModalEvents.triggerModalToggle();
        }

        const cancelSwitchToOnlinePayment = () => {
            switchingToOnlinePayment.value = false;
            organisationPaysByInvoice.value = organisationInvoiceData.value.paysByInvoice;
            isSending.value = false;
        };


        const organisationChanged = (organisation: KlipApi.OrganisationDetail) => {
            organisationDetail.value = organisation;
        };

        const organisationInvoiceDataChanged = async (data: KlipApi.OrganisationInvoiceData) => {
            organisationInvoiceData.value = data;
            organisationPaysByInvoice.value = data.paysByInvoice;

            if (data.athumiData && data.athumiData.hasValidAthumiId) {
                fetchingAthumiData.value = false;
                athumiId.value = data.athumiData.athumiId;
            } else {
                athumiId.value = null;
            }
        }

        watch(() => props.organisation, (newValue) => organisationChanged(newValue), { immediate: true, deep: true });
        watch(() => props.invoiceData, (newValue) => organisationInvoiceDataChanged(newValue), { immediate: true, deep: true });

        const onSubmit = form.handleSubmit(_saveOrganisationInvoiceData);

        return {
            viewModelLoading,
            isSending,
            isSendingDelete,
            fetchingAthumiData,
            isAdmin,
            prepaidAmount,
            togglePaysByInvoiceModal,
            invoiceDataModal,
            athumiId,
            onSaveActor,
            switchingToOnlinePayment,
            organisationDetail,
            organisationInvoiceData,
            organisationPaysByInvoice,
            organisationPaysByInvoiceChanged,
            subOrganisationsCount,
            routeAdminOrganisationDetail,
            toggleCheckbox,
            switchToOnlinePayment,
            switchToInvoice,
            onOpenSwitchToInvoiceModal,
            switchToInvoiceDisabled,
            hasValidAthumiId,
            hasValidPaymentData,
            cancelSwitchToOnlinePayment,
            saveOrganisationPaysByInvoice,
            athumiActor,
            onSubmit,
            disableSubmit,

            fields,
        };
    }
});
